import { createValidatedFieldAtoms } from './formAtoms';
import { validationRules } from './formValidation';
import {
  businessProductAtom,
  cronTimezoneAtom,
  cronTriggerAtom,
  deprecatedAtom,
  descriptionAtom,
  disabledAtom,
  eventParametersMappingAtom,
  ftpDropzoneAtom,
  nameAtom,
  notifyEmailsAtom,
  reportingIndexAtom,
  s3AccessKeyAtom,
  s3DropzoneAtom,
  s3PrivateKeyAtom,
  s3RoleArnAtom,
  s3RoleSessionNameAtom,
  sftpDropzoneAtom,
  sftpPrivateKeyAtom,
  slaveAtom,
  supportResubmitAtom,
  supportsPrioritizationAtom,
  tenantAtom,
  throttleRateAtom,
  timeoutAfterAtom,
  topicIdAtom,
  unpackInputAtom,
} from './workflowFieldAtoms';

/**
 * Validated workflow field atoms
 * These atoms include validation rules and error state management
 *
 * Migration strategy: Incrementally replace atoms from workflowFieldAtoms.ts
 * Start with critical fields (name, description) and expand as needed
 */

const WORKFLOW_NAME_FIELD = 'Workflow name';

// Name field - required, 1-200 characters
export const validatedNameAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [
      validationRules.required(WORKFLOW_NAME_FIELD),
      validationRules.minLength(1, WORKFLOW_NAME_FIELD) as any,
      validationRules.maxLength(200, WORKFLOW_NAME_FIELD) as any,
    ],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: nameAtom as any,
});

// Description field - optional, max 500 characters
export const validatedDescriptionAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(500, 'Description') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: descriptionAtom as any,
});

// Slave field - required
export const validatedSlaveAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.required('Slave')],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: slaveAtom as any,
});

// Throttle rate - required, positive number
export const validatedThrottleRateAtom = createValidatedFieldAtoms<
  number | null,
  number
>({
  validator: {
    rules: [
      validationRules.positive('Throttle rate'),
      validationRules.max(1000, 'Throttle rate'),
    ],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: throttleRateAtom as any,
});

// Business product - optional
export const validatedBusinessProductAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(100, 'Business product') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: businessProductAtom as any,
});

// Cron timezone - optional
export const validatedCronTimezoneAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: cronTimezoneAtom as any,
});

// Cron trigger - optional
export const validatedCronTriggerAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: cronTriggerAtom as any,
});

// Deprecated - optional boolean
export const validatedDeprecatedAtom = createValidatedFieldAtoms<
  boolean,
  boolean
>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: deprecatedAtom as any,
});

// Disabled - optional boolean
export const validatedDisabledAtom = createValidatedFieldAtoms<
  boolean,
  boolean
>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: disabledAtom as any,
});

// Unpack input - optional boolean
export const validatedUnpackInputAtom = createValidatedFieldAtoms<
  boolean,
  boolean
>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: unpackInputAtom as any,
});

// Support resubmit - optional boolean
export const validatedSupportResubmitAtom = createValidatedFieldAtoms<
  boolean,
  boolean
>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: supportResubmitAtom as any,
});

// Supports prioritization - optional boolean
export const validatedSupportsPrioritizationAtom = createValidatedFieldAtoms<
  boolean,
  boolean
>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: supportsPrioritizationAtom as any,
});

// SFTP Dropzone - optional, max 300 characters
export const validatedSftpDropzoneAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(300, 'SFTP Dropzone') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: sftpDropzoneAtom as any,
});

// SFTP Private Key - optional, max 300 characters
export const validatedSftpPrivateKeyAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(300, 'SFTP Private Key') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: sftpPrivateKeyAtom as any,
});

// FTP Dropzone - optional, max 300 characters
export const validatedFtpDropzoneAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(300, 'FTP Dropzone') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: ftpDropzoneAtom as any,
});

// S3 Dropzone - optional, max 300 characters
export const validatedS3DropzoneAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(300, 'S3 Dropzone') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: s3DropzoneAtom as any,
});

// S3 Access Key - optional, max 50 characters
export const validatedS3AccessKeyAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(50, 'S3 Access Key') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: s3AccessKeyAtom as any,
});

// S3 Private Key - optional, max 50 characters
export const validatedS3PrivateKeyAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(50, 'S3 Private Key') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: s3PrivateKeyAtom as any,
});

// S3 Role ARN - optional, max 300 characters
export const validatedS3RoleArnAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(300, 'S3 Role ARN') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: s3RoleArnAtom as any,
});

// S3 Role Session Name - optional, max 300 characters
export const validatedS3RoleSessionNameAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(300, 'S3 Role Session Name') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: s3RoleSessionNameAtom as any,
});

// Notification Emails - optional, max 1000 characters
export const validatedNotificationEmailsAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(1000, 'Notification Emails') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: notifyEmailsAtom as any,
});

// Event Params Mapping - optional, max 1000 characters
export const validatedEventParamsMappingAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(1000, 'Event Params Mapping') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: eventParametersMappingAtom as any,
});

// Topic ID - optional, max 300 characters
export const validatedTopicIdAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(300, 'Topic ID') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: topicIdAtom as any,
});

// Reporting Index - optional, max 100 characters
export const validatedReportingIndexAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(100, 'Reporting Index') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: reportingIndexAtom as any,
});

// Tenant - optional, max 300 characters
export const validatedTenantAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  validator: {
    rules: [validationRules.maxLength(300, 'Tenant') as any],
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: tenantAtom as any,
});

// Timeout After nH - optional number
export const validatedTimeoutAfterAtom = createValidatedFieldAtoms<
  string | null,
  string
>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueAtom: timeoutAfterAtom as any,
});

/**
 * Export all validated atoms for easy import
 */
export const validatedWorkflowFields = {
  businessProduct: validatedBusinessProductAtom,
  cronTimezone: validatedCronTimezoneAtom,
  cronTrigger: validatedCronTriggerAtom,
  deprecated: validatedDeprecatedAtom,
  description: validatedDescriptionAtom,
  disabled: validatedDisabledAtom,
  eventParamsMapping: validatedEventParamsMappingAtom,
  ftpDropzone: validatedFtpDropzoneAtom,
  name: validatedNameAtom,
  notificationEmails: validatedNotificationEmailsAtom,
  reportingIndex: validatedReportingIndexAtom,
  s3AccessKey: validatedS3AccessKeyAtom,
  s3Dropzone: validatedS3DropzoneAtom,
  s3PrivateKey: validatedS3PrivateKeyAtom,
  s3RoleArn: validatedS3RoleArnAtom,
  s3RoleSessionName: validatedS3RoleSessionNameAtom,
  sftpDropzone: validatedSftpDropzoneAtom,
  sftpPrivateKey: validatedSftpPrivateKeyAtom,
  slave: validatedSlaveAtom,
  supportResubmit: validatedSupportResubmitAtom,
  supportsPrioritization: validatedSupportsPrioritizationAtom,
  tenant: validatedTenantAtom,
  throttleRate: validatedThrottleRateAtom,
  timeoutAfter: validatedTimeoutAfterAtom,
  topicId: validatedTopicIdAtom,
  unpackInput: validatedUnpackInputAtom,
};
